import { MainDrawScheduleCode, MainDrawTypeCode } from 'constants/Draw';
import { IPrizeTier, PRIZE_TIERS } from 'constants/LottoPrizeValues';
import { MAIN_PRIZE_CATEGORIES_2026 } from 'constants/PrizeCategories';
import { debounce, DebouncedFunc } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { tryApi } from 'utils/api/common';
import { prizeApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import Chrevon from '../../svg/ChevronLeft15.svg';
import Cross from '../../svg/Cross14.svg';
import Search from '../../svg/Search18.svg';
import Time from '../../svg/Time18.svg';
import { GroupingDropdown } from './GroupingDropdown';
import prizeGroupStyles from './PrizeGroupingHeader.module.css';
import styles from './SearchFilter.module.css';
import { trackPrizeSearchTextbox } from 'utils/analytics/analytics';

export type SearchSuggestion = {
  id: string;
  label: string;
};

export type Prize = {
  id: string;
  name: string;
  tier?: string;
  category?: string;
};

export type PrizeSearchParams = {
  term?: string;
  tier?: string;
  category?: string;
  limit?: number;
  nextCursor?: string;
};

export type PrizeSearchResponse = {
  items: Prize[];
  nextCursor?: string;
};

export type SearchProps = {
  resultsPage?: boolean;
  filterOnly?: boolean;
  searchInput?: string;
  resultCount?: number;
  loadingResults?: boolean;
  placeholder?: string;
  filterPlaceholder?: string;
  minChars?: number; // default 3
  filterType: 'tier' | 'category';
};

type AnyFn = (...args: any[]) => void;

export function useDebounce<T extends AnyFn>(
  callback: T,
  delay = 1000,
): DebouncedFunc<T> {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debounced = useMemo(() => {
    return debounce((...args: Parameters<T>) => {
      callbackRef.current(...args);
    }, delay);
  }, [delay]);

  useEffect(() => {
    return () => debounced.cancel();
  }, [debounced]);

  return debounced;
}

export const SearchFilter: React.FC<SearchProps> = ({
  resultsPage = false,
  filterOnly = false,
  searchInput,
  resultCount,
  loadingResults,
  placeholder = "I'm looking for...",
  filterPlaceholder = 'Categories',
  minChars = 3,
  filterType,
}) => {
  const router = useRouter();
  const [input, setInput] = useState(searchInput ? searchInput : '');
  const [focused, setFocused] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [initialSuggestions, setInitialSuggestions] = useState<string[] | []>(
    [],
  );
  const [recentSearches, setRecentSearches] = useState<string[] | []>([]);
  const [prompts, setPrompts] = useState<{ id: number; title: string }[]>([]);
  const [categoryPrompts, setCategoryPrompts] = useState<
    { categoryCode: string }[]
  >([]);
  const [tierPrompts, setTierPrompts] = useState<{ tierCode: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const filterRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(async (query: string) => {
    if (query.length < minChars) {
      return;
    }
    setLoading(true);

    const res = await tryApi(
      prizeApiClient.public.getPromptPrizes({
        query: {
          term: query,
          drawScheduleCode: MainDrawScheduleCode.TUESDAY,
          drawTypeCode: MainDrawTypeCode,
        },
      }),
    );

    if (res.status === HttpStatusCode.OK) {
      setPrompts(res.body.searchPrompts);
      setCategoryPrompts(res.body.categories);
      setTierPrompts(res.body.tiers);
    }
    setLoading(false);
  }, 300);

  useEffect(() => {
    const getSuggestions = async () => {
      const res = await tryApi(prizeApiClient.public.getSearchSuggestions());

      if (res.status === HttpStatusCode.OK) {
        setInitialSuggestions(res.body);
      }
    };

    const getRecentSearches = () => {
      const storeName = 'prizeSearch';
      const recent = localStorage.getItem(storeName);

      if (recent) {
        let parsedStore;
        try {
          parsedStore = JSON.parse(recent);
        } catch (e) {
          localStorage.removeItem(storeName);
        }

        if (Array.isArray(parsedStore)) {
          setRecentSearches(parsedStore);
        }
      }
    };

    getSuggestions();
    getRecentSearches();
  }, []);

  useEffect(() => {
    const body = document.body;
    const focusElement = document.getElementById('search-focus');

    if (searchFocused && window.innerWidth < 768) {
      const elementPosition = focusElement?.getBoundingClientRect().top;
      const focusedHeight = window.innerHeight - (elementPosition || 0);

      if (focusElement) {
        focusElement.style.height = `${focusedHeight}px`;
      }

      body.style = 'height: 100vh; overflow-y: hidden;';
    } else {
      body.style = '';
      if (focusElement) {
        focusElement.style.height = '';
      }
    }
    return () => {
      body.style = '';
    };
  }, [searchFocused]);

  useEffect(() => {
    if (!filterOpen) return;

    function handleOutsideClick(event: MouseEvent) {
      const target = event.target;

      if (
        target instanceof Node &&
        filterRef.current &&
        !filterRef.current.contains(target)
      ) {
        setFilterOpen(false);
      }
    }

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [filterOpen]);

  const directToResults = async (
    searchType: 'term' | 'category' | 'tier',
    searchByText: string,
    code?: string,
  ) => {
    // Use the current input's state to track what people searched for, even if  we're sending them to somewhere else
    // (i.e don't use the searchByText parameter provided to this function)
    trackPrizeSearchTextbox(input);

    setInput(searchByText);
    setFocused(false);
    setSearchFocused(false);
    setFilterOpen(false);

    return router.push(
      `/prizes/search?${searchType}=${code ? code : searchByText}`,
    );
  };

  const showInitial =
    focused &&
    recentSearches.length === 0 &&
    prompts.length === 0 &&
    categoryPrompts.length === 0 &&
    tierPrompts.length === 0;
  const showPrompts =
    focused &&
    input.length >= minChars &&
    (prompts.length > 0 ||
      categoryPrompts.length > 0 ||
      tierPrompts.length > 0);

  return (
    <div className={`${styles.searchFilterComponent}`}>
      <div className={styles.searchFilterInputs}>
        <label
          className={`${styles.searchInput} ${searchFocused ? styles.searchInputFocused : ''} ${resultsPage && !searchFocused ? styles.searchInputOnResults : ''}`}
        >
          <Search />
          <input
            id="search-input"
            placeholder={placeholder}
            autoComplete="off"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (e.target.value.length < minChars) {
                setPrompts([]);
              }
              debouncedSearch(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && input.length) {
                e.currentTarget.blur();
                directToResults('term', input);
              }
            }}
            onFocus={() => {
              setSearchFocused(true);
              setFocused(true);
              setFilterOpen(false);
            }}
            onBlur={() => {
              if (window.innerWidth >= 768) {
                setFocused(false);
                setSearchFocused(false);
              }
            }}
          />
          {searchFocused && input.length > 0 && (
            <div
              className={styles.crossButton}
              onMouseDown={() => setInput('')}
            >
              <Cross />
            </div>
          )}
          {resultsPage && !searchFocused && input.length > 0 && (
            <div className={styles.resultCount}>
              {!loadingResults &&
                `${resultCount} ${resultCount === 1 ? 'result' : 'results'}`}
            </div>
          )}
          <div
            id="search-focus"
            className={`${focused ? styles.focused : styles.unfocused}`}
          >
            {showPrompts && (
              <div className={styles.searchPrompts}>
                {categoryPrompts.length > 0 && (
                  <div className={styles.searchTypePrompt}>
                    {categoryPrompts.map((p, ind) => {
                      const code = MAIN_PRIZE_CATEGORIES_2026[p.categoryCode];
                      return (
                        <div
                          key={ind}
                          className={`${styles.searchSuggestion} ${styles.searchPrompt}`}
                          onMouseDown={() =>
                            directToResults(
                              'category',
                              code.name,
                              p.categoryCode,
                            )
                          }
                        >
                          <div className={styles.header}>
                            <div className={styles.categoryImage}>
                              {code.svg}
                            </div>
                            <div className={styles.categoryLink}>
                              <h3>{code.name}</h3>
                              <p className={prizeGroupStyles.subtitle}>
                                {code.description}
                              </p>
                            </div>
                            <div className={styles.chevron}>
                              <Chrevon />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {tierPrompts.length > 0 && (
                  <div className={styles.searchTypePrompt}>
                    {tierPrompts.map((p, ind) => {
                      const code: IPrizeTier | undefined = Object.values(
                        PRIZE_TIERS,
                      ).find((prizeTier) => prizeTier.key === p.tierCode);

                      if (!code) {
                        return null;
                      }

                      return (
                        <div
                          key={ind}
                          className={`${styles.searchSuggestion} ${styles.searchPrompt}`}
                          onMouseDown={() =>
                            directToResults('tier', p.tierCode)
                          }
                        >
                          <div className={styles.header}>
                            <img
                              className={styles.categoryImage}
                              src={code.badge}
                            />
                            <div className={styles.categoryLink}>
                              <h3>{code.key}</h3>
                              <p className={prizeGroupStyles.subtitle}>
                                Prizes worth up to
                                <strong>
                                  {' '}
                                  £{code.value.toLocaleString('en-GB')}
                                </strong>
                              </p>
                            </div>
                            <div className={styles.chevron}>
                              <Chrevon />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {prompts.length > 0 && (
                  <div className={styles.searchSuggestions}>
                    {prompts.map((p, ind) => {
                      const highlightWord = (text: string) => {
                        const regex = new RegExp(`(${input})`, 'gi');
                        const parts = text.split(regex);

                        return parts.map((part, index) =>
                          part.toLowerCase() === input?.toLowerCase() ? (
                            <span key={index} className={styles.highlight}>
                              {part}
                            </span>
                          ) : (
                            part
                          ),
                        );
                      };

                      return (
                        <div
                          key={ind}
                          className={`${styles.searchSuggestion} ${styles.searchPrompt}`}
                          onMouseDown={() => directToResults('term', p.title)}
                        >
                          <Search />
                          <div className={styles.searchSuggestionText}>
                            {highlightWord(p.title)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {showInitial && (
              <div className={styles.searchSuggestions}>
                {initialSuggestions.map((s, ind) => (
                  <div
                    key={ind}
                    className={styles.searchSuggestion}
                    onMouseDown={() => directToResults('term', s)}
                  >
                    <Search />
                    <div className={styles.searchSuggestionText}>{s}</div>
                  </div>
                ))}
              </div>
            )}
            {recentSearches.length > 0 && (
              <div className={styles.searchSuggestions}>
                {showPrompts && <div className={styles.recentSearchLine}></div>}
                <div
                  className={`${styles.searchSuggestion} ${styles.searchRecent}`}
                >
                  Recent searches
                </div>
                {recentSearches.map((r, ind) => (
                  <div
                    key={ind}
                    className={styles.searchSuggestion}
                    onMouseDown={() => directToResults('term', r)}
                  >
                    <Time />
                    <div className={styles.searchSuggestionText}>{r}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </label>
        {!resultsPage && !searchFocused && (
          <div className={styles.filter}>
            <GroupingDropdown
              filterType={filterType}
              selectedFilter={undefined}
              setSelectedFilter={(x) => {
                router.push(
                  filterType === 'tier' ? `/prizes?tier=${x}` : `/prizes/${x}`,
                );
              }}
            />
          </div>
        )}
        <button
          className={`${styles.sideButton} ${searchFocused ? styles.showCancel : ''}`}
          onClick={(e) => {
            e.preventDefault();
            setFilterOpen(false);
            setSearchFocused(false);
            setFocused(false);
          }}
        >
          Cancel
        </button>
        {resultsPage && (
          <Link href="/prizes">
            <a
              className={`${styles.sideButton} ${!searchFocused ? styles.showBack : ''}`}
              href="/prizes"
            >
              Back
            </a>
          </Link>
        )}
      </div>
    </div>
  );
};
